import * as XLSX from "xlsx";
import { ExcelGeneratorService } from "./excel-generator.service";
import { QSO, QSOExcelRow } from "@/types/qso.types";
import { ExcelFile } from "@/types/excel.types";
import { CONSTANTS } from "@/config/constants";
import { chunkArray } from "@/utils/string.utils";
import { getTimestamp } from "@/utils/date.utils";
import { formatDate, formatTime } from "@/utils/date.utils";

export class QSOExcelService {
  private generator = new ExcelGeneratorService();

  generateQSOExcel(qsos: QSO[]): ExcelFile[] {
    const batches = chunkArray(qsos, CONSTANTS.BATCH_SIZE);
    const files: ExcelFile[] = [];

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (!batch) continue;

      const workbook = this.createWorkbook(batch);
      const buffer = this.generator.writeWorkbook(workbook);

      if (buffer.byteLength > CONSTANTS.MAX_EXCEL_FILE_SIZE) {
        const smallerBatches = chunkArray(batch, CONSTANTS.BATCH_SIZE / 2);
        for (let j = 0; j < smallerBatches.length; j++) {
          const smallerBatch = smallerBatches[j];
          if (!smallerBatch) continue;

          const smallerWorkbook = this.createWorkbook(smallerBatch);
          const smallerBuffer = this.generator.writeWorkbook(smallerWorkbook);
          files.push(
            this.generator.createFile(
              smallerBuffer,
              `QSO标签_${getTimestamp()}_${i + 1}_${j + 1}.xlsx`
            )
          );
        }
      } else {
        files.push(this.generator.createFile(buffer, `QSO标签_${getTimestamp()}_${i + 1}.xlsx`));
      }
    }

    return files;
  }

  private createWorkbook(qsos: QSO[]): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();
    const data = qsos.map((qso) => this.formatQSOForExcel(qso));
    const worksheet = XLSX.utils.json_to_sheet(data);

    this.generator.freezeFirstRow(worksheet);
    XLSX.utils.book_append_sheet(workbook, worksheet, "QSO标签");

    return workbook;
  }

  private formatQSOForExcel(qso: QSO): QSOExcelRow {
    return {
      call: qso.call,
      date: formatDate(qso.qso_date),
      time: formatTime(qso.time_on),
      freq: this.formatFreq(qso.freq),
      mode: qso.mode,
      rst: this.formatRST(qso),
      req: this.formatREQ(qso.qsl_rcvd),
      comment: this.formatComment(qso),
    };
  }

  private formatFreq(freq: number): number {
    return Math.round(freq * 1000) / 1000;
  }

  private formatRST(qso: QSO): string {
    if (qso.mode === "FT8" || qso.mode === "FT4") {
      return `S:${qso.rst_sent} R:${qso.rst_rcvd}`;
    }
    return qso.rst_sent;
  }

  private formatREQ(qslRcvd: string): string {
    return qslRcvd === "Y" ? "TNX QSL" : "PSE QSL";
  }

  private formatComment(qso: QSO): string {
    const parts: string[] = [];

    if (qso.pota) {
      parts.push(`POTA\r\n${qso.pota}`);
    }

    if (qso.contest_id) {
      parts.push(qso.contest_id);
    }

    return parts.join("\r\n");
  }
}
